import React from 'react';
import classnames from '../../helpers/classnames';
import {PopupContainerProps, PopupContainerState} from '../../@types/components/PopupContainer';
import {PopupContainerContext} from '../../contexts/PopupContainerContext';
import {IPopupContainerContext} from '../../@interfaces/contexts/IPopupContainerContext';
import PopupPortal from './PopupPortal';

export default class PopupContainer extends React.Component<PopupContainerProps, PopupContainerState> {
    static #POPUPS_OPENED_BODY_CLASS_NAME = 'has-opened-popups';

    state: PopupContainerState = {
        openedPopupsNames: [],
    };

    private readonly contextProviderValue!: IPopupContainerContext;
    private readonly popupsContainerRef!: React.RefObject<HTMLDivElement>;

    constructor(props: PopupContainerProps) {
        super(props);

        this.popupsContainerRef = React.createRef<HTMLDivElement>();

        this.contextProviderValue = {
            openPopup: this.openPopup.bind(this),
            closePopup: this.closePopup.bind(this),
            getLastOpenedPopup: (): string | null => this.state.openedPopupsNames.at(-1) ?? null,
            popupsWrapperRef: this.popupsContainerRef,
            Portal: PopupPortal,
        };
    }

    render() {
        const hasOpenedPopups = this.getOpenedPopupsList().length > 0;

        return <div className={classnames({
            'popup-container': true,
            'popup-container--has-opened-popups': hasOpenedPopups,
        })}>
            <div className={classnames('popup-container__popups-wrapper', {
                'd-none': !hasOpenedPopups
            })} ref={this.popupsContainerRef}>
                {this.renderPopups()}
            </div>

            <PopupContainerContext.Provider value={this.contextProviderValue}>
                <div className="popup-container__background-elements">
                    {this.props.children}
                </div>
            </PopupContainerContext.Provider>
        </div>;
    }

    protected renderPopups() {
        const popups = this.getPopups();
        if (!Object.keys(popups).length) {
            return null;
        }

        return Object.keys(popups).map(popupName => {
            if (!this.isPopupOpen(popupName)) {
                return null;
            }

            return <div key={`popup@${popupName}`} className="popup-container__popup">
                <div className="popup-container__popup-content">
                    {popups[popupName]()}
                </div>
            </div>;
        });
    }

    async openPopup(popupName: string): Promise<void> {
        return new Promise(resolve => {
            if (document?.body) {
                document?.body.classList?.add(PopupContainer.#POPUPS_OPENED_BODY_CLASS_NAME);
            }

            this.setState(prev => ({
                openedPopupsNames: [...prev.openedPopupsNames, popupName]
            }), resolve);
        });
    }

    async closePopup(popupName: string): Promise<void> {
        if (!this.isPopupOpen(popupName)) {
            return;
        }

        return new Promise(resolve => {
            const names = this.state.openedPopupsNames.slice(0);
            const index = names.indexOf(popupName);
            if (index >= 0) {
                names.splice(index, 1);
            }

            if (document?.body && !names.length) {
                document?.body.classList?.remove(PopupContainer.#POPUPS_OPENED_BODY_CLASS_NAME);
            }

            this.setState({openedPopupsNames: names}, resolve);
        });
    }

    protected getPopups() {
        return this.props.popups ?? {};
    }

    protected isPopupOpen(popupName: string): boolean {
        return this.getOpenedPopupsList().includes(popupName);
    }

    protected getOpenedPopupsList(): string[] {
        return [
            ...this.props.openedPopups ?? [],
            ...this.state.openedPopupsNames,
        ];
    }
}
